import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { screenSchemas, type UpdateScreenSchema } from '@/api/screens/screen.schema';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { CopyText } from '@/components/ui/copy-text';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormMultiSelect } from '@/components/ui/form-fields/form-multi-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { ContentLoader } from '@/components/ui/loaders';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useScreen, useUpdateScreen } from '@/services/screens';
import type { ValidationError } from '@/types/api/validation-error';
import { ColumnsEditor, FieldsEditor, FiltersEditor } from '../+components/layout-editor';
import { ScreenPreview } from '../+components/preview';

export const Route = createFileRoute('/_app/screens/$id/')({
  head: () => meta({ label }),
  component: RouteComponent,
});

type Schema = UpdateScreenSchema;
const schema = screenSchemas.update;
const model = 'screens';
const label = 'Editar Tela';

const ACTION_OPTIONS = [
  { label: 'Listar (browse)', value: 'browse' },
  { label: 'Criar (create)', value: 'create' },
  { label: 'Visualizar (read)', value: 'read' },
  { label: 'Editar (update)', value: 'update' },
  { label: 'Excluir (delete)', value: 'delete' },
];

function RouteComponent() {
  const router = useRouter();
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useUpdateScreen();

  const { id } = Route.useParams();
  const { data, isLoading: loadingData } = useScreen({ id });

  const form = useForm<any>({
    resolver: zodResolver(schema) as any,
    shouldUseNativeValidation: false,
    defaultValues: {},
  });

  // `data` chega depois da montagem (a query ainda está em andamento no primeiro render) —
  // `defaultValues` do useForm só é lido uma vez, então sem isso o form ficava vazio na
  // primeira visita à página (só preenchia se a query já estivesse em cache de uma visita anterior).
  useEffect(() => {
    if (data) form.reset(data as unknown as Schema);
  }, [data, form]);

  const handleSubmit = (values: Schema) => save({ id, data: values });

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error && error.status === 422) {
      const { errors } = error.value as unknown as ValidationError<Schema>;

      if (errors) {
        Object.entries(errors).forEach(([key, message]) => {
          form.setError(key as keyof Schema, { message });
        });
      }
    }
  }, [error, form]);

  if (loadingData || !data) return <ContentLoader show />;

  const shortId = id?.slice(0, 8);

  return (
    <Content
      breadcrumbs={{
        title: (
          <>
            {label} <CopyText className="ml-2" value={id} label={`${shortId}...`} />
          </>
        ),
        items: [
          { label: 'Listagem', route: `/${model}` },
          { label: 'Editar', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title="Dados da tela"
        description="Configure o recurso, o menu e o layout de listagem/formulário."
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <div className="col-span-full space-y-6">
            <Tabs defaultValue="general">
              <TabsList>
                <TabsTrigger value="general">Geral</TabsTrigger>
                <TabsTrigger value="columns">Colunas</TabsTrigger>
                <TabsTrigger value="filters">Filtros</TabsTrigger>
                <TabsTrigger value="fields">Campos</TabsTrigger>
              </TabsList>

              <TabsContent value="general">
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))] gap-4">
                  <FormInput
                    name="resource"
                    control={form.control}
                    label="Recurso (slug)"
                    disabled
                  />
                  <FormInput
                    name="label"
                    control={form.control}
                    label="Label (singular)"
                    required
                  />
                  <FormInput
                    name="pluralLabel"
                    control={form.control}
                    label="Label (plural)"
                    required
                  />
                  <FormInput name="icon" control={form.control} label="Ícone (lucide)" />
                  <FormInput
                    name="menuOrder"
                    type="number"
                    control={form.control}
                    label="Ordem no menu"
                  />
                  <FormInput name="menuGroup" control={form.control} label="Grupo no menu" />
                  <FormSwitch name="showInMenu" control={form.control} label="Mostrar no menu" />
                  <FormSwitch name="isActive" control={form.control} label="Ativa" />
                  <FormMultiSelect
                    name="actions"
                    control={form.control}
                    label="Ações habilitadas"
                    options={ACTION_OPTIONS}
                    sm={12}
                    md={12}
                    lg={12}
                  />
                </div>
              </TabsContent>

              <TabsContent value="columns">
                <ColumnsEditor control={form.control as any} />
              </TabsContent>

              <TabsContent value="filters">
                <FiltersEditor control={form.control as any} />
              </TabsContent>

              <TabsContent value="fields">
                <FieldsEditor control={form.control as any} />
              </TabsContent>
            </Tabs>

            <ScreenPreview control={form.control as any} />
          </div>
        </FormContent>
        <FormFooter>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="sand"
            size="lg"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
